import {
  createCalendarFeed,
  revokeCalendarFeed,
  useGetCalendar,
  type CalendarEventOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, todayIso } from '../../lib/format';
import { CALENDAR_KIND } from './labels';

const DAYS_SHOWN = 90;

function plusDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const moved = new Date(year, month - 1, day + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${moved.getFullYear()}-${pad(moved.getMonth() + 1)}-${pad(moved.getDate())}`;
}

function monthOf(iso: string): string {
  const [year, month] = iso.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

function FeedSection() {
  const [url, setUrl] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setError(null);
    setMessage(null);
    try {
      setUrl(okData(await createCalendarFeed())?.url ?? null);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function revoke() {
    setError(null);
    try {
      await revokeCalendarFeed();
      setUrl(null);
      setMessage('Your link no longer works.');
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="h6">Add to Your Calendar</Typography>
        <Typography color="text.secondary">
          Subscribe from Google Calendar, Outlook or Apple Calendar. Anyone with the link can see
          these dates, so treat it like a password. Residents&apos; names are left out.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="info">{message}</Alert>}
        {url && (
          <TextField
            fullWidth
            label="Calendar Link"
            value={url}
            helperText="Shown once. Making a new link stops the old one."
            slotProps={{ htmlInput: { readOnly: true } }}
          />
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => void create()}>
            Make a New Link
          </Button>
          <Button color="error" onClick={() => void revoke()}>
            Turn Off My Link
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function CalendarPage() {
  const [start, setStart] = useState(todayIso());
  const end = plusDays(start, DAYS_SHOWN);
  const events = okData(useGetCalendar({ start, end }).data)?.items;
  const months = useMemo(() => {
    const grouped = new Map<string, CalendarEventOut[]>();
    for (const event of events ?? []) {
      const month = monthOf(event.on);
      grouped.set(month, [...(grouped.get(month) ?? []), event]);
    }
    return [...grouped];
  }, [events]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Calendar</Typography>
          <Typography color="text.secondary">
            Court dates, expirations, deadlines and scheduled work, for the next {DAYS_SHOWN} days.
          </Typography>
        </div>
        <TextField
          type="date"
          size="small"
          label="Starting From"
          value={start}
          onChange={(e) => e.target.value && setStart(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      {events?.length === 0 && (
        <Typography color="text.secondary">
          Nothing coming up in these {DAYS_SHOWN} days.
        </Typography>
      )}
      {months.map(([month, items]) => (
        <Paper key={month} variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            {month}
          </Typography>
          <Stack spacing={1.5}>
            {items.map((event) => (
              <Stack
                key={event.uid}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: { sm: 'center' } }}
              >
                <Typography sx={{ flex: '0 0 110px', fontWeight: 500 }}>
                  {formatDate(event.on)}
                </Typography>
                <div style={{ flex: '0 0 140px' }}>
                  <Chip
                    size="small"
                    color={CALENDAR_KIND[event.kind].color}
                    label={CALENDAR_KIND[event.kind].label}
                  />
                </div>
                <div style={{ flexGrow: 1 }}>
                  <MuiLink component={Link} to={event.link}>
                    {event.title}
                  </MuiLink>
                  {event.detail && (
                    <Typography variant="body2" color="text.secondary">
                      {event.detail}
                    </Typography>
                  )}
                </div>
              </Stack>
            ))}
          </Stack>
        </Paper>
      ))}
      <FeedSection />
    </Stack>
  );
}
